import { useCallback, useEffect, useMemo, useRef } from "react";
import { Snackbar, Alert } from "@mui/material";
import Button from "../Button/Button";

const AppSnackbar = ({
  open = false,
  type = "info", // success | error | warning | info
  message = "",
  autoHideDuration = 2000,
  anchorOrigin = { vertical: "top", horizontal: "center" },
  showAction = false,
  actionText = "OK",
  onClose,
  onAction,
  sx = {},
}) => {
  const baseZIndex = 11000; // above mobile burger and modal overlay

  const computedSx = useMemo(() => {
    const baseStyle = {
      zIndex: baseZIndex,
      display: "flex",
      width: "100%",
      justifyContent: "center",
      alignItems: "flex-start",
      // Mobile gets extra breathing room; tablet/desktop keep header alignment.
      top: {
        xs: "max(10px, env(safe-area-inset-top))",
        sm: "max(8px, env(safe-area-inset-top))",
        md: "max(8px, env(safe-area-inset-top))",
      },
      left: 0,
      right: 0,
      px: { xs: 1.5, sm: 0 }, // mobile-only side gutters
      boxSizing: "border-box",
      pointerEvents: "none", // allow clicks through container; alert keeps pointer events
      "& .MuiAlert-root": {
        pointerEvents: "auto",
        margin: "0 auto",
        width: "auto",
        maxWidth: {
          xs: "calc(100vw - 24px)",
          sm: "min(640px, calc(100vw - 24px))",
        },
      },
    };

    if (typeof sx === "function") {
      return (theme) => {
        const resolved = sx(theme) || {};
        const providedZ = typeof resolved.zIndex === "number" ? resolved.zIndex : baseZIndex;
        return {
          ...baseStyle,
          ...resolved,
          zIndex: Math.max(baseZIndex, providedZ),
        };
      };
    }
    const providedZ = typeof sx?.zIndex === "number" ? sx.zIndex : baseZIndex;
    return {
      ...baseStyle,
      ...(sx || {}),
      zIndex: Math.max(baseZIndex, providedZ),
    };
  }, [sx, baseZIndex]);

  const adjustedAutoHideDuration = useMemo(() => {
    if ((type === "error" || type === "warning") && autoHideDuration <= 2000) {
      return 4000;
    }
    return autoHideDuration;
  }, [autoHideDuration, type]);

  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const remainingRef = useRef(adjustedAutoHideDuration);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(
    (event, reason) => {
      if (reason === "clickaway") return;
      clearTimer();
      onClose?.(event, reason);
    },
    [clearTimer, onClose]
  );

  const startTimer = useCallback(
    (duration) => {
      if (!open || duration == null) return;
      clearTimer();
      remainingRef.current = duration;
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        handleClose(null, "timeout");
      }, duration);
    },
    [clearTimer, handleClose, open]
  );

  useEffect(() => {
    if (open && adjustedAutoHideDuration != null) {
      startTimer(adjustedAutoHideDuration);
    } else {
      clearTimer();
    }

    return clearTimer;
  }, [adjustedAutoHideDuration, clearTimer, open, startTimer]);

  const pauseTimer = useCallback(() => {
    if (!timerRef.current || startTimeRef.current == null) return;
    const elapsed = Date.now() - startTimeRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimer();
  }, [clearTimer]);

  const resumeTimer = useCallback(() => {
    if (!open || adjustedAutoHideDuration == null) return;
    if (remainingRef.current <= 0) {
      handleClose(null, "timeout");
      return;
    }
    startTimer(remainingRef.current);
  }, [adjustedAutoHideDuration, handleClose, open, startTimer]);

  const interactionHandlers = {
    onMouseEnter: pauseTimer,
    onMouseLeave: resumeTimer,
    onFocus: pauseTimer,
    onBlur: resumeTimer,
    onTouchStart: pauseTimer,
    onTouchEnd: resumeTimer,
    onPointerEnter: pauseTimer,
    onPointerLeave: resumeTimer,
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={null}
      onClose={handleClose}
      anchorOrigin={anchorOrigin}
      sx={computedSx}
      {...interactionHandlers}
    >
      <Alert
        onClose={handleClose}
        severity={type}
        variant="filled"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "auto",
          maxWidth: {
            xs: "calc(100vw - 24px)",
            sm: "min(640px, calc(100vw - 24px))",
          },
          minWidth: 0,
          textAlign: "left",
          py: { xs: 0.75, sm: 0.75 },
          px: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          boxShadow: "0 10px 28px rgba(0, 0, 0, 0.28)",
          backdropFilter: "blur(1px)",
          "& .MuiAlert-message": {
            width: "auto",
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            lineHeight: 1.35,
            fontWeight: 500,
            pr: 0.5,
            py: 0,
            mt: 0,
            textAlign: "center",
            fontSize: {
              xs: "0.9rem",
              sm: "0.95rem",
            },
          },
          "& .MuiAlert-icon": {
            marginRight: { xs: 1, sm: 1.25 },
            padding: 0,
            marginTop: 0,
            alignSelf: "center",
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          },
          "& .MuiAlert-action": {
            alignItems: "center",
            marginTop: 0,
            paddingTop: 0,
            paddingLeft: 0.5,
            marginLeft: 0.25,
            marginRight: -0.25,
            "& .MuiIconButton-root": {
              color: "rgba(255, 255, 255, 0.95)",
              padding: {
                xs: "3px",
                sm: "6px",
              },
            },
          },
        }}
        action={
          showAction ? (
            <Button
              variant="primary"
              size="small"
              sx={{ minWidth: 70, textTransform: "none" }}
              // onClick={() => {
              //   onAction?.();
              //   onClose?.();
              // }}
              onClick={(event) => {
                onAction?.();
                handleClose(event, "action");
              }}
            >
              {actionText}
            </Button>
          ) : null
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default AppSnackbar;
